import React from "react";
import { Link } from "react-router-dom";
import { Clock, Hand, Package, ScrollText, Mail } from "lucide-react";

export default function ReturnPolicy() {
  return (
    <main className="relative max-w-3xl mx-auto px-6 md:px-12 py-20" data-testid="return-policy-page">
      <header className="text-center mb-14">
        <div className="tracking-celestial text-[var(--rb-gold)] mb-3">Crafted with reverence · Sold with honesty</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--rb-text-heading)]">
          Return &amp; Refund Policy
        </h1>
        <p className="mt-6 font-serif-display italic text-[18px] text-[var(--rb-text-body)] leading-relaxed">
          Every diorama is shaped by hand, hour by patient hour. This policy holds room
          for both sides — your peace of mind, and the time poured into each commission.
        </p>
        <div className="ornament mt-8"><span>✦</span></div>
      </header>

      <section className="glass p-8 md:p-10 rounded-sm mb-8" data-testid="policy-section-deposit">
        <div className="flex items-start gap-4">
          <Clock size={22} className="text-[var(--rb-gold)] mt-1 shrink-0" strokeWidth={1.5}/>
          <div>
            <h2 className="font-serif-display text-2xl text-[var(--rb-text-heading)] mb-3">
              The $50 deposit — a 2-hour window
            </h2>
            <p className="font-serif-display italic text-[16px] text-[var(--rb-text-body)] leading-relaxed mb-4">
              When you place a commission, work begins almost immediately — sketching, sourcing,
              and shaping. Because of this, the deposit is treated with care from the moment it&apos;s placed.
            </p>
            <ul className="space-y-3 text-[14px] text-[var(--rb-text-body)] leading-relaxed">
              <li className="flex gap-3"><span className="text-[var(--rb-gold)] mt-1">✦</span><span><strong className="text-[var(--rb-text-heading)]">Within 2 hours of placing your order:</strong> Your $50 deposit is fully refundable — no questions asked — should buyer&apos;s remorse arise.</span></li>
              <li className="flex gap-3"><span className="text-[var(--rb-gold)] mt-1">✦</span><span><strong className="text-[var(--rb-text-heading)]">After 2 hours:</strong> The deposit becomes non-refundable, as work toward your diorama will have already begun.</span></li>
              <li className="flex gap-3"><span className="text-[var(--rb-gold)] mt-1">✦</span><span><strong className="text-[var(--rb-text-heading)]">If the studio cancels:</strong> If for any reason we choose not to take on your commission, your deposit is returned to you in full.</span></li>
            </ul>
          </div>
        </div>
      </section>

      <section className="glass p-8 md:p-10 rounded-sm mb-8" data-testid="policy-section-finished">
        <div className="flex items-start gap-4">
          <Hand size={22} className="text-[var(--rb-gold)] mt-1 shrink-0" strokeWidth={1.5}/>
          <div>
            <h2 className="font-serif-display text-2xl text-[var(--rb-text-heading)] mb-3">
              Completed dioramas — final sale
            </h2>
            <p className="font-serif-display italic text-[16px] text-[var(--rb-text-body)] leading-relaxed mb-4">
              Each diorama is a one-of-a-kind heirloom, built only for the pet it honors.
              Because no two pieces are alike — and because every hour of handwork is sealed
              permanently into the final piece — completed commissions are not eligible for
              refund or remake on the basis of dissatisfaction.
            </p>
            <p className="font-serif-display italic text-[14px] text-[var(--rb-text-muted)] leading-relaxed">
              We&apos;ll work closely with you throughout the process — sharing progress photos
              and welcoming your input — so that the final diorama honors them just as you envision.
            </p>
          </div>
        </div>
      </section>

      <section className="glass p-8 md:p-10 rounded-sm mb-8" data-testid="policy-section-shipping">
        <div className="flex items-start gap-4">
          <Package size={22} className="text-[var(--rb-gold)] mt-1 shrink-0" strokeWidth={1.5}/>
          <div>
            <h2 className="font-serif-display text-2xl text-[var(--rb-text-heading)] mb-3">
              If it arrives damaged in transit
            </h2>
            <p className="font-serif-display italic text-[16px] text-[var(--rb-text-body)] leading-relaxed mb-4">
              We pack every diorama with painstaking care, but the road home can be unkind. If
              your piece arrives damaged, we will <strong className="text-[var(--rb-text-heading)]">repair or fully remake it at no cost to you</strong>.
            </p>
            <ul className="space-y-3 text-[14px] text-[var(--rb-text-body)] leading-relaxed">
              <li className="flex gap-3"><span className="text-[var(--rb-gold)] mt-1">✦</span>Please contact us within <strong className="text-[var(--rb-text-heading)]">7 days of delivery</strong> with photos of the damage and the original packaging.</li>
              <li className="flex gap-3"><span className="text-[var(--rb-gold)] mt-1">✦</span>We&apos;ll cover return shipping and the cost of restoration or recreation.</li>
              <li className="flex gap-3"><span className="text-[var(--rb-gold)] mt-1">✦</span>Where a faithful remake isn&apos;t possible, we&apos;ll discuss alternatives together.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="glass p-8 md:p-10 rounded-sm mb-8" data-testid="policy-section-process">
        <div className="flex items-start gap-4">
          <ScrollText size={22} className="text-[var(--rb-gold)] mt-1 shrink-0" strokeWidth={1.5}/>
          <div>
            <h2 className="font-serif-display text-2xl text-[var(--rb-text-heading)] mb-3">
              How to request a refund
            </h2>
            <p className="font-serif-display italic text-[16px] text-[var(--rb-text-body)] leading-relaxed mb-4">
              For eligible refunds, please reach out as soon as you can. Approved refunds are
              returned to your original method of payment and typically appear within 5–10
              business days, depending on your bank.
            </p>
            <a href="mailto:Theheirloompet@gmail.com?subject=Refund%20request" className="inline-flex items-center gap-2 text-[var(--rb-gold)] hover:text-[var(--rb-text-heading)] transition-colors font-serif-display italic text-[15px]" data-testid="policy-contact-email">
              <Mail size={16} strokeWidth={1.5}/> Theheirloompet@gmail.com
            </a>
          </div>
        </div>
      </section>

      <div className="text-center mt-16 text-[12px] text-[var(--rb-text-muted)] italic">
        Last updated · June 2026 ·{" "}
        <Link to="/commission" className="text-[var(--rb-gold)] hover:text-[var(--rb-text-heading)] transition-colors" data-testid="policy-back-commission">
          Return to commission a diorama
        </Link>
      </div>
    </main>
  );
}
